import AppCopy from "copy/AppCopy";
import { UserCardProps } from "declarations/card";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCustomer } from "reducers/userReducer";
import { Paths } from "router/Paths";

function UserCard({ user, isNew = false, onSeen }: UserCardProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const seenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { emailText, nameText, phoneText, statusText } = AppCopy.usersPage;

  const handleOnPressCustomer = () => {
    dispatch(setCustomer(user));
    navigate(`${Paths.users}/${user._id}`);
  };

  const isUserSuspended = (): boolean => {
    let status = false;
    const currentDate = new Date();
    if (user?.isActive === false) {
      status = true;
    }
    if (user?.postingPrivilageSuspension) {
      const suspensionDate = new Date(user.postingPrivilageSuspension);
      if (suspensionDate.getTime() > currentDate.getTime()) {
        status = true;
      }
    }
    return status;
  };

  useEffect(() => {
    if (!isNew || !onSeen || !user?._id) return;

    const clearSeenTimer = () => {
      if (seenTimerRef.current) {
        clearTimeout(seenTimerRef.current);
        seenTimerRef.current = null;
      }
    };

    const markSeenAfterDelay = () => {
      if (seenTimerRef.current) return;
      seenTimerRef.current = setTimeout(() => {
        onSeen(String(user._id));
        seenTimerRef.current = null;
      }, 1500);
    };

    if (!("IntersectionObserver" in window)) {
      markSeenAfterDelay();
      return clearSeenTimer;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          markSeenAfterDelay();
        } else {
          clearSeenTimer();
        }
      },
      { threshold: [0, 0.6] }
    );

    if (rowRef.current) observer.observe(rowRef.current);

    return () => {
      clearSeenTimer();
      observer.disconnect();
    };
  }, [isNew, onSeen, user?._id]);

  return (
    <div ref={rowRef} className={Classname.container} onClick={handleOnPressCustomer}>
      <div className={Classname.phoneNo}>
        <span className={Classname.title}>{phoneText}</span>
        <div className={Classname.phoneValueRow}>
        <span className={Classname.carTag}>{user.phoneNumber || "No phone"}</span>
          {isNew && <span className={Classname.newBadge}>New</span>}
        </div>
      </div>
      <div className={Classname.fullName}>
        <span className={Classname.title}>{nameText}</span>
        <span>{user.name || "Unnamed user"}</span>
      </div>
      <div className={Classname.email}>
        <span className={Classname.title}>{emailText}</span>
        <span className="break-all">{user.email || "No email"}</span>
      </div>
      <div className={Classname.status}>
        <span className={Classname.title}>{statusText}</span>
        <span>{isUserSuspended() ? "Suspended" : "Active"}</span>
      </div>
    </div>
  );
}

export default UserCard;

const Classname = {
  container:
    "flex w-full flex-shrink-0 cursor-pointer flex-col justify-between border-b border-slate-100 px-2 py-3 text-sm text-secondaryText transition-colors hover:bg-slate-50 sm:min-h-14 sm:flex-row sm:items-center",
  phoneNo: "w-full sm:w-1/5 my-1 sm:my-0 ",
  fullName: "w-full sm:w-1/5 my-1 sm:my-0 ",
  email: "w-full sm:w-1/5 flex flex-col sm:flex-row my-1 sm:my-0 ",
  status: "w-full sm:w-1/5 flex flex-col sm:flex-row my-1 sm:my-0 ",

  title: "flex sm:hidden my-2 text-xs text-secondaryText",
  phoneValueRow: "flex items-center gap-2",

  carTag:
    "flex items-center justify-center w-fit px-2 bg-bgSecondary rounded-md text-primaryText",
  carTagBold: "font-bold",
  newBadge:
    "w-fit rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white",
};
